import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { get, set, del, createStore, type UseStore } from 'idb-keyval';
import superjson from 'superjson';
import { STORAGE_KEYS } from '@/services/supabase/config';

/**
 * Offline cache persistence for the POS.
 *
 * Why this exists: the in-memory TanStack Query cache (8h gcTime) is lost on a cold
 * app start. Persisting it to IndexedDB lets the app boot and show tabs/products/org
 * while offline. We also persist paused (queued) mutations so offline writes survive
 * an app restart once mutations are registered with mutationKeys (later phases).
 *
 * Bump this when the cache shape changes in a backwards-incompatible way so stale
 * persisted data is discarded instead of rehydrated.
 */
const CACHE_VERSION = 'v2';

/** Discard the persisted cache if it is older than this (defensive upper bound). */
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

/**
 * IndexedDB store. A device stays on a single org, but we still scope the cache key
 * by org slug so the rare account/org switch cannot leak another tenant's data.
 */
const idbStore: UseStore = createStore('tabstr-query-cache', 'cache');

const getScopedKey = (): string => {
  let slug: string | null;
  try {
    slug = localStorage.getItem(STORAGE_KEYS.currentOrganizationSlug);
  } catch {
    slug = null;
  }
  return `tabstr-query-cache:${slug ?? 'default'}`;
};

const hasIndexedDb = typeof indexedDB !== 'undefined';

/** In-memory fallback for environments without IndexedDB (tests, private modes). */
const memoryStore = new Map<string, string>();

/**
 * idb-keyval-backed AsyncStorage. The persister calls a single key; we resolve it
 * lazily per call so it always matches the currently connected org.
 */
const indexedDbStorage = {
  getItem: async (): Promise<string | null> => {
    if (!hasIndexedDb) return memoryStore.get(getScopedKey()) ?? null;
    const value = await get<string>(getScopedKey(), idbStore);
    return value ?? null;
  },
  setItem: async (_key: string, value: string): Promise<void> => {
    if (!hasIndexedDb) {
      memoryStore.set(getScopedKey(), value);
      return;
    }
    await set(getScopedKey(), value, idbStore);
  },
  removeItem: async (): Promise<void> => {
    if (!hasIndexedDb) {
      memoryStore.delete(getScopedKey());
      return;
    }
    await del(getScopedKey(), idbStore);
  },
};

/**
 * superjson serialization preserves Date instances (and Map/Set). The default
 * JSON serializer would turn `createdAt`/`updatedAt` into strings, breaking any
 * `.getTime()`/date formatting on rehydrated tabs and sales.
 *
 * Reports, cash history, and messages are not persisted — they rewrite a large
 * IndexedDB blob and are not required to reopen a tab after a kill. Tabs,
 * products, tables layout, org, and auth stay in the persisted cache for offline.
 */
export const HIGH_CHURN_QUERY_ROOTS = new Set([
  'cashBalances',
  'shiftClosures',
  'cashWithdrawals',
  'reservations',
  'messages',
  'headerMessages',
  'acceptedOrdersHistory',
  'tabItemDeletionHistory',
  'tabDeletionHistory',
  'discountApplicationHistory',
  'invoiceStatus',
]);

export const shouldPersistQuery = (query: {
  queryKey: readonly unknown[];
  state: { status: string };
}): boolean => {
  // Only persist settled, successful query data — never errors or in-flight state.
  if (query.state.status !== 'success') return false;
  const root = query.queryKey[0];
  return !(typeof root === 'string' && HIGH_CHURN_QUERY_ROOTS.has(root));
};

const persister = createAsyncStoragePersister({
  storage: indexedDbStorage,
  serialize: (data) => superjson.stringify(data),
  deserialize: (data) => superjson.parse(data),
  // Avoid blocking the UI thread with frequent writes during rapid POS actions.
  throttleTime: 3000,
});

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  maxAge: MAX_AGE_MS,
  buster: CACHE_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: shouldPersistQuery,
  },
};

/** Clears the persisted cache for the current org (call on sign-out / org switch). */
export const clearPersistedQueryCache = async (): Promise<void> => {
  if (!hasIndexedDb) {
    memoryStore.delete(getScopedKey());
    return;
  }
  await del(getScopedKey(), idbStore);
};
